/**
 * The library's own glyphs, transcribed from KANON `Programy v2 - D3 (0.8)` path for path.
 *
 * All of them are decorative: each sits beside a word that says the same thing, or inside a button
 * that carries its whole name. None of them is ever the only thing telling a trainer what a row is.
 *
 * The `+` on the header's primary square is NOT here — it is the same drawing on this screen and on
 * the clients roster, so it lives with the band they share, in `shared/ui/ScreenHeader`.
 */

import type { PlanCategoryKey } from '~/entities/plan-draft';

interface IconProps {
  readonly className?: string;
}

/** The canon's own attributes for the two 20-unit mode marks. */
const MODE_PROPS = {
  viewBox: '0 0 20 20',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

/**
 * A plan on the calendar: a month with three days marked.
 *
 * The three dots are FILLED and carry `stroke="none"` of their own — at 13px a stroked 0.95-radius
 * circle is a grey ring rather than a mark.
 */
export function CalendarModeIcon({ className }: IconProps) {
  return (
    <svg className={className} {...MODE_PROPS}>
      <rect x="3.2" y="5" width="13.6" height="11.8" rx="2.2" />
      <path d="M6.6 3.4v2.4M13.4 3.4v2.4M3.2 8.8h13.6" />
      <circle cx="7" cy="12" r="0.95" fill="currentColor" stroke="none" />
      <circle cx="10.4" cy="12" r="0.95" fill="currentColor" stroke="none" />
      <circle cx="13.8" cy="12" r="0.95" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** A plan at the client's own pace: a loop with two arrowheads, which is the canon's self-paced mark. */
export function SelfPacedModeIcon({ className }: IconProps) {
  return (
    <svg className={className} {...MODE_PROPS}>
      <path d="M5.4 6.6h8.2a2.4 2.4 0 012.4 2.4v2M14.6 13.4H6.4A2.4 2.4 0 014 11V9" />
      <path d="m12.4 4.4 2.4 2.2-2.4 2.2M7.6 15.6l-2.4-2.2 2.4-2.2" />
    </svg>
  );
}

/** One person, beside the count of how many follow a plan. */
export function PersonIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <circle cx="10" cy="6.6" r="3" />
      <path d="M4.4 16.4c0-3.1 2.5-5 5.6-5s5.6 1.9 5.6 5" />
    </svg>
  );
}

/* The filter mark moved to `shared/ui/FilterIcon` on its second consumer: the exercise
 * library's drawer drew the identical path. Re-exported so this barrel keeps its shape and
 * the library's toolbar needs no edit. */
export { FilterIcon } from '~/shared/ui/FilterIcon';

/**
 * The six category marks the filter panel's Typ chips carry, transcribed path for path.
 *
 * Keyed by `PlanCategoryKey` so the panel maps over the app's own category order rather than
 * repeating six branches, and looked up by that key at the call site — a category this build has
 * never heard of has no chip to draw, so there is no fallback glyph and no need for one.
 *
 * Decorative, like every other mark here: each sits beside its category's own word.
 */
const CATEGORY_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

/** The path each category's mark is drawn from, in the canon's own coordinates. */
const CATEGORY_PATHS: Readonly<Record<PlanCategoryKey, readonly string[]>> = {
  strength: ['M4 9v6M7.5 6.5v11M7.5 12h9M16.5 6.5v11M20 9v6'],
  cardio: ['M3 12h3.4l1.7-5 3.3 11 2-6 1.3 3H21'],
  hiit: ['M13 2 4.5 13.5H11l-1 8.5L19.5 9.5H13z'],
  flexibility: ['M9 6.5c0 3 0 4 2.5 5l3.5 1.5M9 11.5 6 21M9 11.5 12 17'],
  bodyweight: ['M12 7v6.5M12 13.5 8.5 21M12 13.5 15.5 21M6 9.5l6 1.5 6-1.5'],
  hybrid: ['M16 3h5v5M4 20 21 3M21 16v5h-5m-1-6 6 6M4 4l5 5'],
};

/** The two marks whose figure needs a head; the canon draws it as a separate circle. */
const CATEGORY_HEADS: Partial<Record<PlanCategoryKey, { cx: number; cy: number; r: number }>> = {
  flexibility: { cx: 9, cy: 4.5, r: 2 },
  bodyweight: { cx: 12, cy: 4.5, r: 2.2 },
};

interface CategoryIconProps extends IconProps {
  readonly category: PlanCategoryKey;
}

export function CategoryIcon({ category, className }: CategoryIconProps) {
  const head = CATEGORY_HEADS[category];
  return (
    <svg className={className} {...CATEGORY_PROPS}>
      {head === undefined ? null : <circle cx={head.cx} cy={head.cy} r={head.r} />}
      {CATEGORY_PATHS[category].map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );
}
